// ============================================================
// TaskProvenance.tsx — "Created by …" + "Assigned (to you) by …" lines for a
// task's detail / read-only view and edit form.
//
//   • Created by — ALWAYS shown, from Task.createdBy (the server sends the
//     creator's name + isActive; firmUsers is the fallback). A creator who has
//     left the firm reads "Name (former member)".
//   • Assigned by — Task.leadAssignedBy, derived server-side from the audit
//     log (lib/taskProvenance): who made the CURRENT lead the lead. Shown only
//     while it still describes the current lead (a live patch may have moved
//     the lead on since) and never for a lead who took it on themselves.
// ============================================================
import React from 'react'
import type { Task } from '../../types/tasks.types'
import type { FirmUser } from '../../types/user.types'

type Named = { firstName?: string | null; lastName?: string | null; email?: string | null; isActive?: boolean | null }
const nameOf = (u: Named | undefined | null): string =>
  u ? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'Someone') : 'Someone'

// "Name" / "Name (former member)" / "you" for the task's creator.
export const creatorLabel = (t: Task, firmUsers: FirmUser[], currentUserId: string, you = 'you'): string => {
  if (t.createdByUserId === currentUserId) return you
  const u: Named | undefined = t.createdBy ?? firmUsers.find((x) => x.id === t.createdByUserId)
  if (!u) return 'a former member'
  return `${nameOf(u)}${u.isActive === false ? ' (former member)' : ''}`
}

const TaskProvenance: React.FC<{ task: Task; firmUsers: FirmUser[]; currentUserId: string; className?: string }> = ({ task, firmUsers, currentUserId, className = '' }) => {
  const by = task.leadAssignedBy && task.leadAssignedBy.leadId === task.assignedToUserId ? task.leadAssignedBy : null
  const byName = by ? (by.userId === currentUserId ? 'you' : `${by.name}${by.formerMember ? ' (former member)' : ''}`) : null
  return (
    <div className={`text-[12px] leading-[1.5] text-casevo-text-soft ${className}`}>
      {byName && (
        <div>{task.assignedToUserId === currentUserId ? 'Assigned to you by ' : 'Assigned by '}<span className="font-medium text-casevo-mid">{byName}</span></div>
      )}
      <div>Created by <span className="font-medium text-casevo-mid">{creatorLabel(task, firmUsers, currentUserId)}</span></div>
    </div>
  )
}

export default TaskProvenance
